import _ from 'lodash'
import React, { useState } from 'react'
import useError from '@vero/ui-core-browser/hooks/error.js'
import useRedirectParam from '@vero/ui-core-browser/hooks/redirect-param.js'
import useSubmit from '@vero/ui-core-browser/hooks/submit.js'
import Button from '@vero/ui-components/components/button.jsx'
import Input from '@vero/ui-components/components/input.jsx'
import HeaderWithVero from '@vero/ui-components/components/header-with-vero.jsx'
import useClaims from '../hooks/claims.js'
import useEmail from '../hooks/email.js'
import useProviders from '../hooks/providers.js'
import PasswordInput from '../components/password-input.jsx'
import Providers from '../components/providers.jsx'
import SubmitButton from '../components/submit-button.jsx'
import EmailInput from '../components/email-input.jsx'

export default function ({ title, className }) {
  const { redirect } = useRedirectParam()

  const [loading, setLoading] = useState(false)
  const { error, setError, handleError } = useError()
  const { email, setEmail, clearEmail } = useEmail()
  const { providers, hasEmailProvider, isSsoUser } = useProviders(email, setLoading, setError, handleError)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [showCodeInput, setShowCodeInput] = useState(false)
  const { authClient, storeTokens } = useClaims()

  const onSubmit = useSubmit(setLoading, setError, handleError, async () => {
    if (email && !_.isEmpty(providers)) {
      if (password && code) {
        await storeTokens(await authClient.setPassword(email, password, code), redirect)
      } else if (hasEmailProvider()) {
        await authClient.resetPassword(email)
        setShowCodeInput(true)
      }
    }
  })

  return (
    <div className={className}>
      {error && <div className='alert alert-error'>{error}</div>}

      <div className='p-8'>
        <HeaderWithVero title={title} />

        <form onSubmit={onSubmit} className='mb-6'>
          <div className='flex flex-col gap-4'>
            <EmailInput
              className='grow'
              value={email}
              onChange={e => {
                setError(null)
                setEmail(e?.target?.value)
              }}
              onClear={() => {
                setError(null)
                clearEmail()
                setCode('')
                setPassword('')
                setShowCodeInput(false)
              }}
              required
            />

            {hasEmailProvider() && (
              <>
                {showCodeInput && (
                  <Input
                    type='text'
                    name='code'
                    label='confirmation code'
                    className='grow'
                    autoComplete='one-time-code'
                    inputMode='numeric'
                    value={code}
                    minLength={6}
                    onChange={e => {
                      setError(null)
                      setCode(e.target.value.replace(/\s/g, ''))
                    }}
                    onClear={() => {
                      setError(null)
                      setCode('')
                      setPassword('')
                    }}
                    required
                  />
                )}

                {code && (
                  <>
                    <PasswordInput
                      label='new password'
                      className='grow'
                      value={password}
                      onChange={e => {
                        setError(null)
                        setPassword(e.target.value)
                      }}
                      required
                    />
                  </>
                )}
              </>
            )}

            <div className='flex flex-col gap-2'>
              {(_.isEmpty(providers) || hasEmailProvider()) && (
                <div className='flex flex-col'>
                  {!showCodeInput && (
                    <SubmitButton title='send recovery code' loading={loading} />
                  )}

                  {!showCodeInput && email && (
                    <Button
                      type='button'
                      className='btn-link uppercase'
                      onClick={() => setShowCodeInput(true)}
                    >
                      i have a recovery code
                    </Button>
                  )}

                  {showCodeInput && (
                    <SubmitButton title={title} loading={loading} />
                  )}
                </div>
              )}

              {email && isSsoUser() && (
                <p className='alert alert-info'>Your user is a single sign-on user and does not need a password. Please use one of your providers below to sign in.</p>
              )}

              <Providers providers={providers} />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
